import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCcwDotIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn, formatBadgeNumber } from "@/lib/utils";
import { toast } from "sonner";

import { useNetworkState } from "@uidotdev/usehooks";
import { useAuthValue } from "@/stores/auth-store";
import { useUserValue } from "@/stores/user-store";
// import { syncFarmers } from "@/stores/farmer-store";
import { syncOutgrowerFarmers } from "@/stores/outgrower-farmer-store";
import { syncOutgrowerMonitoring } from "@/stores/outgrower-monitoring-store";
import { db } from "@/stores/indexed-db";
import { useLiveQuery } from "dexie-react-hooks";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

export const SyncButton = ({ header }: { header?: boolean }) => {
    const [syncing, setSyncing] = useState(false);
    // const unsyncedFarmers = useLiveQuery(() => db.farmers.where("status").equals("unsynced").or("status").equals("partial-sync").count()) ?? 0;
    const unsyncedOutgrowerFarmers = useLiveQuery(() => db.outgrower_farmers.where("status").equals("unsynced").or("status").equals("partial-sync").count()) ?? 0;
    const unsyncedQuestionnaires = useLiveQuery(() => db.outgrower_monitoring_questionnaires.where("status").equals("unsynced").or("status").equals("partial-sync").count()) ?? 0;
    const totalUnsynced = unsyncedOutgrowerFarmers + unsyncedQuestionnaires;

    const isAuthenticated = useAuthValue("isAuthenticated");
    const accessToken = useAuthValue("accessToken");
    const user = useUserValue("name") === "Administrator" ? useUserValue("name") : useUserValue("email");

    const networkState = useNetworkState();

    const handleSync = async () => {
        if (!isAuthenticated || !accessToken || !user || !networkState.online) {
            return;
        }

        setSyncing(true);
        try {
            // await syncFarmers(accessToken, user);
            await syncOutgrowerFarmers(accessToken, user);
            await syncOutgrowerMonitoring(accessToken, user);
        } catch (err) {
            console.error("Manual sync failed", err);
            toast.error("Manual sync failed. Check your connection and try again.");
        } finally {
            setSyncing(false);
        }
    }

    return (
        <div className="flex flex-col justify-center items-center gap-2">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button variant="outline" onClick={handleSync} disabled={!isAuthenticated || !accessToken || !user || !networkState.online || syncing || totalUnsynced === 0}>
                        <RefreshCcwDotIcon size={16} className={cn("animate-spin", syncing ? "" : "animate-none")} />
                        {header ? "" : totalUnsynced > 0 ? "Sync Now" : "Synced"}
                        <Badge variant="outline" className={cn("h-6 w-8 text-xs text-center text-foreground", totalUnsynced > 0 ? "bg-amber-500/50" : "hidden")}>{formatBadgeNumber(totalUnsynced)}</Badge>
                    </Button>
                </TooltipTrigger>
                <TooltipContent sideOffset={8}>
                    <div className="flex flex-col gap-1 min-w-[10rem]">
                        {/* <div className="flex items-center justify-between gap-4"><span>Farmers</span><span className="font-semibold">{unsyncedFarmers}</span></div> */}
                        <div className="flex items-center justify-between gap-4"><span>Outgrower Farmers</span><span className="font-semibold">{unsyncedOutgrowerFarmers}</span></div>
                        <div className="flex items-center justify-between gap-4"><span>Questionnaires</span><span className="font-semibold">{unsyncedQuestionnaires}</span></div>
                        <div className="h-px my-1 bg-primary-foreground/20" />
                        <div className="flex items-center justify-between gap-4"><span>Total</span><span className="font-semibold">{totalUnsynced}</span></div>
                    </div>
                </TooltipContent>
            </Tooltip>
            <p className={cn("hidden", !header && (!isAuthenticated || !accessToken || !user || !networkState.online) ? "block text-xs text-center text-muted-foreground italic" : "hidden")}>Login required to perform sync</p>
        </div>
    );
}
